import { getProjectContent, useAddWorkflowCheckpoint } from '@/features/projects';
import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import defaultWorkflow from '../misc/defaultWorkflow.json';
import { ENVIRONMENT } from '@/config';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useUploadFilesInFolder } from '@/features/projects/api/uploadFilesInFolder';
import { updateApiConfigOfWorkflow } from './updateApiConfigOfWorkflow';
import { useUpdateProjectContent } from '@/features/projects/api/updateProjectContent';
import { useAppContext } from '@/context/AppContext';

const useWorkflowOperations = (folder_id?: string) => {
  const [commitMessage, setCommitMessage] = useState<string>('');
  const [isSwitchingWorkflow, setIsSwitchingWorkflow] = useState<boolean>(false);
  const [isCommitChangesModalOpen, setIsCommitChangesModalOpen] = useState<boolean>(false);
  const [savedWorkflow, setSavedWorkflow] = useState<any>({});
  const [savedApiWorkflow, setSavedApiWorkflow] = useState<any>({});
  const [isApisLoading, setIsApisLoading] = useState<boolean>(false);
  const [closeCommitChangesModal, setCloseCommitChangesModal] = useState<boolean>(false);
  const [isSwitchingGpuMode, setIsSwitchingGpuMode] = useState<boolean>(false);
  const [workflowFileLoading, setWorkflowFilesLoading] = useState<boolean>(false);
  const [workflowLatesVersion, setWorkflowLatestVersion] = useState<number>(0);

  const { projectId } = useParams<{ projectId: any }>();
  const {
    mutate: uploadFile,
    isLoading: isCreatingWorkflow,
    error: errorCreatingWorkflow,
  } = useUploadFilesInFolder(projectId);
  const {
    mutate: updateFile,
    isLoading: isUpdatingWorkflow,
    error: errorUpdatingWorkflow,
  } = useUpdateProjectContent();
  const { loggedInUsername } = useAppContext();

  const eventCounter = useRef(0);

  const getProjectState = (projectId: string): any => {
    const state = localStorage.getItem('projectState');
    return state ? JSON.parse(state)[projectId] : null;
  };

  const setProjectState = (projectId: string, state: any) => {
    if (!projectId) {
      throw new Error('Invalid projectId: projectId cannot be null or undefined.');
    }
    const projectState = localStorage.getItem('projectState');
    const parsedState = projectState ? JSON.parse(projectState) : {};
    parsedState[projectId] = state;
    localStorage.setItem('projectState', JSON.stringify(parsedState));
  };

  const hasWorkflowChanged = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.hasWorkflowChanged ?? false;
  };

  const setHasWorkflowChanged = (projectId: string, workflowChanged: any): void => {
    // console.log('[DEBUG] Setting hasWorkflowChanged:', projectId, workflowChanged);
    const projectState = getProjectState(projectId) || {};
    projectState.hasWorkflowChanged = workflowChanged;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflow = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedWorkflow ?? {};
  };

  const setSelectedWorkflow = (projectId: string, workflow: any): void => {
    // console.log('[DEBUG] Setting selected workflow:', projectId, workflow);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedWorkflow = workflow;

    setProjectState(projectId, projectState);
  };

  const getSelectedAPIWorkflow = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedAPIWorkflow ?? {};
  };

  const setSelectedAPIWorkflow = (projectId: string, workflow: any): void => {
    // console.log('[DEBUG] Setting selected workflow:', projectId, workflow);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedAPIWorkflow = workflow;

    setProjectState(projectId, projectState);
  };

  const isNewWorkflow = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.isNewWorkflow ?? false;
  };

  const setIsNewWorkflow = (projectId: string, value: any): void => {
    // console.log('[DEBUG] Setting isNewWorkflow:', projectId, value);
    const projectState = getProjectState(projectId) || {};
    projectState.isNewWorkflow = value;
    setProjectState(projectId, projectState);
  };

  const liveChangedWorkflowData = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.liveChangedWorkflowData ?? null;
  };

  const setLiveChangedWorkflowData = (projectId: string, data: any): void => {
    // if(data) console.log('[DEBUG] Setting liveChangedWorkflowData:', projectId, data);
    const projectState = getProjectState(projectId) || {};
    projectState.liveChangedWorkflowData = data;
    setProjectState(projectId, projectState);
  };

  const liveChangedAPIWorkflowData = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.liveChangedAPIWorkflowData ?? null;
  };

  const setLiveChangedAPIWorkflowData = (projectId: string, data: any): void => {
    const projectState = getProjectState(projectId) || {};
    projectState.liveChangedAPIWorkflowData = data;
    setProjectState(projectId, projectState);
  };

  const setSelectedWorkflowId = (projectId: string, workflowId: string | null): void => {
    // console.log('[DEBUG] Setting selected workflow ID:', projectId, workflowId);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedWorkflowId = workflowId;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflowId = (projectId: string): string | null => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedWorkflowId ?? null;
  };

  const setSelectedWorkflowApiFileId = (projectId: string, apiFileId: string | null): void => {
    // console.log('[DEBUG] Setting selected workflow API file ID:', projectId, apiFileId);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedWorkflowApiFileId = apiFileId;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflowApiFileId = (projectId: string): string | null => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedWorkflowApiFileId ?? null;
  };

  const setSelectedWorkflowFileId = (projectId: string, fileId: string | null): void => {
    // console.log('[DEBUG] Setting selected workflow file ID:', projectId, fileId);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedWorkflowFileId = fileId;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflowFileId = (projectId: string): string | null => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedWorkflowFileId ?? null;
  };

  const getComfyuiGpuMode = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.comfyuiGpuMode ?? 'CPU';
  };

  const setComfyuiGpuMode = (projectId: string, value: any): void => {
    const projectState = getProjectState(projectId) || {};
    projectState.comfyuiGpuMode = value;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflowFolderId = (projectId: string): any => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedFolderId ?? null;
  };

  const setSelectedWorkflowFolderId = (projectId: string, folderId: string | null): void => {
    const projectState = getProjectState(projectId) || {};
    projectState.selectedFolderId = folderId;
    setProjectState(projectId, projectState);
  };

  const setSelectedWorkflowFolderName = (projectId: string, name: string | null): void => {
    // console.log('[DEBUG] Setting selected workflow name:', projectId, name);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedFolderName = name;
    setProjectState(projectId, projectState);
  };

  const getSelectedWorkflowFolderName = (projectId: string): string | null => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedFolderName ?? null;
  };

  const setSelectedWorkflowName = (projectId: string, name: string | null): void => {
    // console.log('[DEBUG] Setting selected workflow name:', projectId, name);
    const projectState = getProjectState(projectId) || {};
    projectState.selectedWorkflowName = name;
    setProjectState(projectId, projectState);
  };
  const getSelectedWorkflowName = (projectId: string): string | null => {
    const projectState = getProjectState(projectId);
    return projectState?.selectedWorkflowName ?? null;
  };

  const fetchSelectedWorkflowState = () => {
    getProjectContent({ projectId, folder_id });
  };

  useEffect(() => {
    fetchSelectedWorkflowState();
  }, []);

  const captureWorkflowChanges = () => {
    const selectedWorkflow = getSelectedWorkflow(projectId);
    const existingData: any = liveChangedWorkflowData(projectId) || '{}';
    const parsedWorkflow = existingData ? existingData : null;

    setLiveChangedWorkflowData(projectId, {
      ...parsedWorkflow,
      name: selectedWorkflow?.name,
      _id: selectedWorkflow?._id,
    });

    // localStorage.setItem(
    //   'liveChangedWorkflowData',
    //   JSON.stringify({
    //     ...parsedWorkflow,
    //     name: selectedWorkflow?.name,
    //     path: selectedWorkflow?.path,
    //   })
    // );
  };

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (projectId) {
      if (hasWorkflowChanged(projectId) && !isSwitchingGpuMode) {
        // Start fetching the workflow every 3 seconds
        intervalRef.current = setInterval(async () => {
          // console.log('[DEBUG] Saved to local storage');
          await fetchWorkflowsForSaving();
          captureWorkflowChanges();
        }, 500);
      }

      if (!hasWorkflowChanged(projectId)) {
        setLiveChangedWorkflowData(projectId, null);
      }

      // Stop the interval if isSwitchingGpuMode is true
      if (isSwitchingGpuMode && intervalRef.current) {
        clearInterval(intervalRef.current);
        console.log('[DEBUG] Polling stopped due to GPU mode switching');
        intervalRef.current = null;
      }
    }

    // Cleanup function to clear interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hasWorkflowChanged(projectId), isSwitchingGpuMode]);

  useEffect(() => {
    /**
     * If the workflow is new, set the selectedWorkflow to null and load the default workflow after 3 seconds.
     * This is to ensure that the default workflow is loaded when the user creates a new workflow.
     */
    if (isNewWorkflow(projectId)) {
      setSelectedWorkflow(projectId, null);
      setSelectedAPIWorkflow(projectId, null);
      setSelectedWorkflowId(projectId, null);
      setSelectedWorkflowName(projectId, null);
      setSelectedWorkflowApiFileId(projectId, null);
      setSelectedWorkflowFileId(projectId, null);
      setSelectedWorkflowFolderName(projectId, null);
      setSelectedWorkflowFolderId(projectId, null);
      setHasWorkflowChanged(projectId, false);
      setTimeout(() => {
        console.log('[DEBUG] Loading default workflow', isNewWorkflow(projectId));
        // Empty the live changed workflow
        // localStorage.removeItem('liveChangedWorkflowData');
        setLiveChangedWorkflowData(projectId, null);
        setLiveChangedAPIWorkflowData(projectId, null);
        loadSimpleGraphData(defaultWorkflow);
      }, 2000);
    }
  }, [isNewWorkflow(projectId)]);

  // Create a debounced function
  const debouncedResetSwitchingWorkflow = debounce(() => {
    if (eventCounter.current === 0) {
      // if(ENVIRONMENT === 'development') console.log('[DEBUG] Counter reset');
      setIsSwitchingWorkflow(false);
      fetchSelectedWorkflowState();
    }
  }, 1000); // Adjust the delay as needed

  const getFormattedWorkflowName = (name: string) => {
    const formattedName = name.replace(/_/g, ' ').replace('.json', '');
    return formattedName
      .split(' ')
      .map((word) => {
        return word === 'api' ? '' : word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === 'newWorkflowLoaded') {
        eventCounter.current += 1;
        // if(ENVIRONMENT === 'development') console.log('[DEBUG] New Workflow Loaded', event.data.data);

        // Decrement the counter after a short delay
        setTimeout(() => {
          eventCounter.current -= 1;
          debouncedResetSwitchingWorkflow();
        }, 1000); // Adjust the delay as needed
      }
      if (event.data.type === 'workflowChanged') {
        // if(ENVIRONMENT === 'development') console.log('[DEBUG] Is Switching', isSwitchingWorkflow);
        if (!isSwitchingWorkflow && !isSwitchingGpuMode) {
          setHasWorkflowChanged(projectId, true);
          // if (ENVIRONMENT === 'development') console.log('[DEBUG] Workflow Changed', event.data);
        } else {
          setHasWorkflowChanged(projectId, false);
          // localStorage.removeItem('liveChangedWorkflowData');
          setLiveChangedWorkflowData(projectId, null);
          setLiveChangedAPIWorkflowData(projectId, null);
          // if(ENVIRONMENT === 'development') console.log('[DEBUG] Is SWITHCING');
        }
      }
      if (event.data.type === 'workflowSaved') {
        const saveWorkflow = JSON.parse(event.data.data.normal);
        const saveApiWorkflow = JSON.parse(event.data.data.api);
        const selectedWorkflow = getSelectedWorkflow(projectId);

        // if (ENVIRONMENT === 'development')
        // console.log('[DEBUG] Workflow fetched');
        // localStorage.setItem(
        //   'liveChangedWorkflowData',
        //   JSON.stringify({
        //     name: selectedWorkflow?.name,
        //     path: selectedWorkflow?.path,
        //     content: saveWorkflow,
        //   })
        // );
        setLiveChangedWorkflowData(projectId, {
          name: selectedWorkflow?.name,
          file_id: selectedWorkflow?._id,
          content: saveWorkflow,
        });
        setLiveChangedAPIWorkflowData(projectId, {
          name: selectedWorkflow?.name,
          file_id: selectedWorkflow?._id,
          content: saveApiWorkflow,
        });
        if (saveWorkflow && saveApiWorkflow) {
          setSavedApiWorkflow(saveApiWorkflow);
          setSavedWorkflow(saveWorkflow);
          // if (ENVIRONMENT === 'development')
          //   console.log('[DEBUG] Workflow fetched for saving', saveWorkflow, saveApiWorkflow);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isSwitchingWorkflow]);

  const loadWorkflow = (workflow: any, disableConfirmation: boolean = false) => {
    if (hasWorkflowChanged(projectId) && !isSwitchingGpuMode && !disableConfirmation) {
      const confirmMessage =
        'You have unsaved changes. Are you sure you want to load the new workflow?';
      if (!window.confirm(confirmMessage)) {
        setIsSwitchingWorkflow(false);
        return;
      }
      // Set hasWorkflowChanged to false if the user confirms the message
      setHasWorkflowChanged(projectId, false);
    }

    const iframe = document.getElementById('comfyUIIframe') as HTMLIFrameElement;
    if (iframe && iframe?.contentWindow) {
      // let jsonWorkflow = workflow;
      // if (typeof workflow === 'string') {
      //   jsonWorkflow = JSON.parse(workflow);
      // }
      // Decode the content field safely
      // const data =
      //   typeof jsonWorkflow === 'string'
      //     ? workflowDecodeFromBase64(jsonWorkflow)
      //     : workflow;

      const data = workflow;

      // console.log('[DEBUG] Loading workflowData', workflowData);

      iframe?.contentWindow?.postMessage(
        {
          type: 'loadGraphData',
          data, // workflow content
        },
        '*'
      );
    }
  };

  const loadSimpleGraphData = (workflow: object) => {
    const iframe = document.getElementById('comfyUIIframe') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'loadGraphData', data: workflow }, '*');
    }
  };

  const fetchWorkflowsForSaving = async () => {
    const iframe = document.getElementById('comfyUIIframe') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'saveWorkflow' }, '*');
    }
  };

  const addSingleMutation = useAddWorkflowCheckpoint();

  const saveWorkflow = async (newWorkflowDetails: any = {}) => {
    setIsApisLoading(true);
    if (ENVIRONMENT === 'development')
      console.log('[DEBUG] Saving changes', savedWorkflow, savedApiWorkflow);
    if (
      isNewWorkflow(projectId) ||
      (liveChangedWorkflowData(projectId) && !getSelectedWorkflowName(projectId))
    ) {
      setIsApisLoading(true);
      if (ENVIRONMENT === 'development')
        console.log('[DEBUG] Creating new workflow', newWorkflowDetails);

      // Convert savedWorkflow and savedApiWorkflow to File objects
      const workflowBlob = new Blob([JSON.stringify(savedWorkflow)], { type: 'application/json' });
      const apiWorkflowBlob = new Blob([JSON.stringify(savedApiWorkflow)], {
        type: 'application/json',
      });

      const workflowFile = new File([workflowBlob], `${newWorkflowDetails.name}.json`, {
        type: 'application/json',
      });
      const apiWorkflowFile = new File([apiWorkflowBlob], `${newWorkflowDetails.name}_api.json`, {
        type: 'application/json',
      });

      const workflow_id = uuidv4();
      uploadFile(
        {
          projectId,
          folder_name: newWorkflowDetails?.folder_name,
          folder_id: newWorkflowDetails.folder_id,
          workflow_id,
          workflow_name: newWorkflowDetails.name,
          file: workflowFile,
        },
        {
          onSuccess: (normalRes: any) => {
            // Create the API workflow file
            uploadFile(
              {
                projectId,
                folder_id: newWorkflowDetails.folder_id,
                folder_name: newWorkflowDetails?.folder_name,
                workflow_id,
                is_api_file: true,
                workflow_name: newWorkflowDetails.name,
                file: apiWorkflowFile,
              },
              {
                onSuccess: async (apiRes: any) => {
                  await updateApiConfigOfWorkflow({
                    projectId,
                    workflow_id,
                    savedApiWorkflow,
                    addSingle: ({ project_id, payload }) =>
                      addSingleMutation.mutate({ project_id, payload }),
                    commitAuthor: loggedInUsername,
                    commitMessage,
                    normal_workflow_file_id: normalRes?.id,
                    api_workflow_file_id: apiRes?.id,
                    newVersion: 1,
                    workflowName: apiRes?.workflow_name,
                    onSuccess: async () => {
                      setIsNewWorkflow(projectId, false);
                      setHasWorkflowChanged(projectId, false);

                      fetchSelectedWorkflowState();
                      setSelectedWorkflow(projectId, newWorkflowDetails);
                      setSelectedAPIWorkflow(projectId, savedApiWorkflow);
                      setSelectedWorkflowApiFileId(projectId, apiRes?.id);
                      setSelectedWorkflowId(projectId, workflow_id);
                      setSelectedWorkflowFileId(projectId, normalRes?.id);
                      setSelectedWorkflowName(projectId, apiRes?.workflow_name);
                      setWorkflowFilesLoading(false);
                      setIsApisLoading(false);
                      setCloseCommitChangesModal(true);
                    },
                    onError: (error) => {
                      console.error('Error updating API config:', error);
                      setIsApisLoading(false);
                      setWorkflowFilesLoading(false);
                    },
                  });
                },
              }
            );
          },
        }
      );
    } else {
      setIsApisLoading(true);
      // Convert savedWorkflow and savedApiWorkflow to File objects
      const workflowBlob = new Blob([JSON.stringify(savedWorkflow)], { type: 'application/json' });
      const apiWorkflowBlob = new Blob([JSON.stringify(savedApiWorkflow)], {
        type: 'application/json',
      });

      const workflowFile = new File([workflowBlob], `${newWorkflowDetails.name}.json`, {
        type: 'application/json',
      });
      const apiWorkflowFile = new File([apiWorkflowBlob], `${newWorkflowDetails.name}_api.json`, {
        type: 'application/json',
      });
      updateFile(
        {
          file: workflowFile,
          file_id: getSelectedWorkflowFileId(projectId) ?? '',
          project_id: projectId,
          is_workflow: true,
        },
        {
          onSuccess: async (normalRes: any) => {
            if (!getSelectedWorkflowApiFileId(projectId)) {
              uploadFile(
                {
                  projectId,
                  folder_id: getSelectedWorkflowFolderId(projectId) ?? '',
                  folder_name: getSelectedWorkflowFolderName(projectId) ?? '',
                  workflow_id: getSelectedWorkflowId(projectId) ?? '',
                  is_api_file: true,
                  workflow_name: getSelectedWorkflowName(projectId) ?? '',
                  file: apiWorkflowFile,
                },
                {
                  onSuccess: async (apiRes: any) => {
                    await updateApiConfigOfWorkflow({
                      projectId,
                      workflow_id: getSelectedWorkflowId(projectId) ?? '',
                      savedApiWorkflow,
                      addSingle: ({ project_id, payload }) =>
                        addSingleMutation.mutate({ project_id, payload }),
                      commitAuthor: loggedInUsername,
                      commitMessage,
                      normal_workflow_file_id: normalRes?.id,
                      api_workflow_file_id: apiRes?.id,
                      newVersion: 1,
                      workflowName: apiRes?.workflow_name,
                      onSuccess: async () => {
                        setIsNewWorkflow(projectId, false);
                        setHasWorkflowChanged(projectId, false);

                        fetchSelectedWorkflowState();
                        setSelectedWorkflow(projectId, newWorkflowDetails);
                        setSelectedAPIWorkflow(projectId, savedApiWorkflow);
                        setSelectedWorkflowApiFileId(projectId, apiRes?.id);
                        setSelectedWorkflowId(projectId, getSelectedWorkflowId(projectId));
                        setSelectedWorkflowFileId(projectId, normalRes?.id);
                        setSelectedWorkflowName(projectId, apiRes?.workflow_name);
                        setWorkflowFilesLoading(false);
                        setIsApisLoading(false);
                        setCloseCommitChangesModal(true);
                      },
                      onError: (error) => {
                        console.error('Error updating API config:', error);
                        setIsApisLoading(false);
                        setWorkflowFilesLoading(false);
                      },
                    });
                  },
                }
              );
            } else {
              // Updating API workflow
              await updateFile(
                {
                  file: apiWorkflowFile,
                  file_id: getSelectedWorkflowApiFileId(projectId) ?? '',
                  project_id: projectId,
                  is_workflow: true,
                },
                {
                  onSuccess: async (apiRes: any) => {
                    await updateApiConfigOfWorkflow({
                      projectId,
                      workflow_id: getSelectedWorkflowId(projectId) ?? '',
                      savedApiWorkflow,
                      addSingle: ({ project_id, payload }) =>
                        addSingleMutation.mutate({ project_id, payload }),
                      commitAuthor: loggedInUsername,
                      commitMessage,
                      normal_workflow_file_id: normalRes?.id,
                      api_workflow_file_id: apiRes?.id,
                      newVersion: apiRes?.version,
                      onSuccess: async () => {
                        setHasWorkflowChanged(projectId, false);
                        fetchSelectedWorkflowState();
                        setIsNewWorkflow(projectId, false);
                        setSelectedWorkflow(projectId, newWorkflowDetails);
                        setSelectedAPIWorkflow(projectId, newWorkflowDetails);
                        setIsApisLoading(false);
                        setWorkflowFilesLoading(false);
                        setCloseCommitChangesModal(true);
                      },
                      onError: (error) => {
                        console.error('Error updating API config:', error);
                        setIsApisLoading(false);
                        setWorkflowFilesLoading(false);
                      },
                    });
                  },
                }
              );
            }
          },
        }
      );
    }
    setWorkflowFilesLoading(false);
    setCloseCommitChangesModal(false);
  };

  return {
    loadWorkflow,
    saveWorkflow,
    getSelectedWorkflow,
    setSelectedWorkflow,
    getSelectedAPIWorkflow,
    setSelectedAPIWorkflow,
    hasWorkflowChanged,
    setHasWorkflowChanged,
    isCommitChangesModalOpen,
    setIsCommitChangesModalOpen,
    setCommitMessage,
    commitMessage,
    isSwitchingWorkflow,
    getFormattedWorkflowName,
    fetchWorkflowsForSaving,
    fetchSelectedWorkflowState,
    isSavingWorkflow: isUpdatingWorkflow || isCreatingWorkflow,
    errorSavingWorkflow: errorUpdatingWorkflow || errorCreatingWorkflow,
    isNewWorkflow,
    setIsNewWorkflow,
    isApisLoading,
    closeCommitChangesModal,
    setCloseCommitChangesModal,
    setIsSwitchingGpuMode,
    setIsSwitchingWorkflow,
    isSwitchingGpuMode,
    liveChangedWorkflowData,
    setLiveChangedWorkflowData,
    liveChangedAPIWorkflowData,
    setLiveChangedAPIWorkflowData,
    getComfyuiGpuMode,
    setComfyuiGpuMode,
    setWorkflowFilesLoading,
    workflowFileLoading,
    workflowLatesVersion,
    setSelectedWorkflowId,
    getSelectedWorkflowId,
    setSelectedWorkflowApiFileId,
    getSelectedWorkflowApiFileId,
    setSelectedWorkflowName,
    getSelectedWorkflowName,
    setWorkflowLatestVersion,
    setSelectedWorkflowFileId,
    getSelectedWorkflowFileId,
    setSelectedWorkflowFolderId,
    getSelectedWorkflowFolderId,
    setSelectedWorkflowFolderName,
  };
};

export default useWorkflowOperations;
